import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import Chip from '@mui/material/Chip'

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount)

type BudgetItem = {
  category: string
  spent: number
  budgeted: number
}

const pctColor = (pct: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (pct === null) return 'default'
  if (pct >= 100) return 'error'
  if (pct >= 80) return 'warning'
  return 'success'
}

const BudgetProgressList: React.FC<{ items: BudgetItem[] }> = ({ items }) => (
  <Box>
    {items.map((item, index) => {
      const pct = item.budgeted > 0 ? Math.round((item.spent / item.budgeted) * 100) : null
      const color = pctColor(pct)
      return (
        <Box
          key={item.category}
          sx={{
            pt: index === 0 ? 0 : 1.25,
            mt: index === 0 ? 0 : 1.25,
            borderTop: index === 0 ? 'none' : '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.category}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <b style={{ color: 'inherit', fontWeight: 600 }}>{formatSek(item.spent)}</b>
              {item.budgeted > 0 ? ` of ${formatSek(item.budgeted)}` : ' (no budget set)'}
              {pct !== null && (
                <Chip
                  label={`${pct}%`}
                  size="small"
                  color={color}
                  variant={color === 'default' ? 'outlined' : 'filled'}
                  sx={{ height: 18, fontSize: '0.65rem', ml: 1, fontWeight: 700 }}
                />
              )}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(pct ?? 0, 100)}
            color={color === 'default' ? 'inherit' : color}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )
    })}
  </Box>
)

export default BudgetProgressList
